import { routeConstants, codetableBreadcrumbs } from '../../routes/route-constants';
import isObject from 'lodash/isobject';
import { Location, History } from 'history';
import * as routeUrls from './route-urls';
import { toAbsoluteUrl } from 'app/infrastructure/hosting';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { getResolver } from 'app/area/shared/codetable/get-resolver';

let historySingleton: any;

/**
 * Instead of relying on the history instance react-router assigns to props
 * we ensure that we use that same, configured store for scenarios where we
 * don't have component props avaliable.
 */
export function useHistory(historyObject: History) {
  historySingleton = historyObject;
}

export function toStartupView(replaceCurrent: boolean = false) {
  replaceCurrent ? replace(routeConstants.startupView) : to(routeConstants.startupView);
}

export function toCaseView(caseNumber: number, replaceCurrent: boolean = false, newWindow: boolean = false) {
  const url = routeUrls.toCaseView(caseNumber);
  replaceCurrent ? replace(url) : (newWindow? window.open(toAbsoluteUrl(url)): to(url));
}

export function toCaseEdit(caseNumber: number, entityPath: string = '') {
  const l = historySingleton.getCurrentLocation();
  to({ pathname: routeUrls.toCaseEdit(caseNumber, entityPath), search: l.search });
}

export function toCaseDuplicate(caseNumber: number) {
  to(routeUrls.toCaseDuplicate(caseNumber));
}

export function toCodeSelectView(breadcrumbs: number[]) {
  to(routeUrls.toCodeSelectView(codetableBreadcrumbs(breadcrumbs)));
}

export function toCodeList(rootId: number, optionsCollectionId?: number, categoryId?: number) {
  const url = `/administration/optionsCollection/${rootId ? rootId + '/' : ''}${isDefinedIncludingZero(optionsCollectionId)
    ? optionsCollectionId : ''}`;
  window.open(toAbsoluteUrl(categoryId ? url + `?categoryId=${categoryId}` : url));
}

export function toCodeListWithBreadcrumbs(breadcrumbs: number[], categoryId?: number) {

  const resolver = getResolver(breadcrumbs);
  const url = resolver?.(breadcrumbs) ??
    '/administration/optionsCollection' + codetableBreadcrumbs(breadcrumbs);

  window.open(toAbsoluteUrl(categoryId ? url + `?categoryId=${categoryId}` : url));
}

export function toCreateCase(caseTypeId: number) {
  replace(routeUrls.toCreateCase(caseTypeId));
}

export function toPrintCaseView(caseUrl: string) {
  const current = historySingleton.getCurrentLocation();

  const url = historySingleton.createHref({
    pathname: caseUrl || current.pathname,
    query: {
      ...current.query,
      expandAll: true,
      print: true
    }
  });
  if (window) {
    window.open(url,
      'Print window',
      'menubar=no,location=no,resizable=no,scrollbars=no,status=no,width=800,height=800');
  }
}

/**
 * Redirect to a draft, with a optional query string parameter
 * @param draftId
 * @param querystring passing undefined preserces the current client `location.search` as query string
 */
export function toDraft(draftId: number, queryParams?: any, entityPath: string = '') {
  const { query } = queryParams || historySingleton.getCurrentLocation();
  replace({ pathname: routeUrls.toDraft(draftId, entityPath), query });
}

export function toCaseListing(
  favouriteId: number, report?: number, openFilters?: boolean,
  editSavedFavourite?: boolean, replaceUrl?: boolean
) {
  const route = replaceUrl ? replace : to;
  route({
    pathname: routeUrls.toCaseListing(favouriteId),
    query: {
      ...(report ? { report: String(report) } : {}),
      ...(openFilters ? { openFilters: 'true' } : {}),
      ...(editSavedFavourite ? { editSavedFavourite: 'true' } : {})
    }
  });
}

export function toUpdateSavedReport(favouriteId: number, newWindow: boolean = false) {
  newWindow
    ? window.open(toAbsoluteUrl(routeUrls.toUpdateSavedReport(favouriteId)))
    : to(routeUrls.toUpdateSavedReport(favouriteId));
}

export function toCompanyReportDraftFavourite(
  favouriteId: number, companyReportId: number, editSavedFavourite?: boolean, replaceUrl?: boolean) {
  const route = replaceUrl ? replace : to;
  route({
    pathname: routeUrls.toCompanyReportDraftFavourite(favouriteId, companyReportId),
    query: {
      ...(editSavedFavourite ? { editSavedFavourite: 'true' } : {})
    }
  });
}

export function toUpdateSavedCompanyReport(favouriteId: number, companyReportId: number) {
  replace({
    pathname: routeUrls.toUpdateSavedCompanyReport(favouriteId, companyReportId)
  });
}

export function toSavedCompanyReport(favouriteId: number, companyReportId: number) {
  replace({
    pathname: routeUrls.toSavedCompanyReport(favouriteId, companyReportId)
  });
}

export function toSearchView(newWindow: boolean = false) {
  newWindow
    ? window.open(toAbsoluteUrl(routeConstants.createFavouriteDraft))
    : to(routeConstants.createFavouriteDraft);
}

export function toSearchViewWithQuery(query: string) {
  to(routeUrls.toSearchViewWithQuery(query));
}

export function toDashboardView(dashboardType: number) {
  to(routeUrls.toDashboardView(dashboardType));
}

export function toBowtie(caseNumber: number, newWindow: boolean = false) {
  const url = `/bowtie/${caseNumber}`;
  newWindow ? window.open(toAbsoluteUrl(url)) : to(url);
}

export function toBowtieConnection(caseNumber: number, searchArg: string) {
  const { query } = historySingleton.getCurrentLocation();
  const search = `${searchArg}` + (query.section ? `&returnToSection=${query.section}` : '');
  to({ pathname: `${routeConstants.bowtie}/${caseNumber}`, search, state: { internal: true } });
}

export function toAdminUsersList(internal: boolean = true) {
  to({ pathname: routeConstants.userSearchView, state: { internal } });
}

export function toAdminRoleList(internal: boolean = true) {
  to({ pathname: routeConstants.rolesView, state: { internal } });
}

export function toAdminMessagesList() {
  to({ pathname: routeConstants.administrationMessagesList, state: { internal: false } });
}

export function toDuplicateRole(id: number) {
  to({ pathname: routeConstants.roleDuplicateFor(id), state: { internal: false } });
}

export function toDuplicateUser(id: number) {
  to({ pathname: routeConstants.userDuplicateFor(id), state: { internal: false } });
}

export function toSelectConnected(searchArg: string) {
  const { query } = historySingleton.getCurrentLocation();
  const search = `${searchArg}&returnToSection=${query.section}`;
  to({ pathname: routeConstants.selectConnectedCaseType, search, state: { internal: true } });
}

export function toConnectionInitiator(route: Partial<Location>) {
  const { query } = historySingleton.getCurrentLocation();
  const search = route.search ? `${route.search}&section=${query.returnToSection}` : `?section=${query.returnToSection}`;
  to({ ...route, search });
}

export function toSelectChangeCaseType(draftId: number) {
  return () => to({
    pathname: routeConstants.selectChangeCaseType,
    query: { draftId: draftId.toString() },
    state: { internal: true }
  }
  );
}

export const toSearch = (options: { [key: string]: string }) => {

  const { pathname, query } = historySingleton.getCurrentLocation();
  const routeArg = { pathname: pathname, query: { ...query, ...options } } as Location;

  to(routeArg);
};

export function toSectionURL(id: number | string, explicitPath?: string) {
  const { pathname: currentPath } = historySingleton.getCurrentLocation();
  const pathname = explicitPath || currentPath;
  return `${pathname}?section=${id}`;
}

export function toSection(id: number | string, explicitPath?: string) {
  const { query: currentQuery, pathname: currentPath } = historySingleton.getCurrentLocation();
  if (currentQuery.section === id) {
    return;
  }
  const pathname = explicitPath || currentPath;
  // Assume we should clear params of source view when outside path
  const query = explicitPath ? undefined : currentQuery;
  // Only replace when navigating internally within a document
  const route = explicitPath ? to : replace;
  route({ pathname, query: { ...query, section: id }, state: { internal: !explicitPath } });
}

export function toAllSections() {
  const { query, pathname } = historySingleton.getCurrentLocation();
  replace({ pathname, query: { ...query, expandAll: true }, state: { internal: true } });
}

export function toCurrentSection() {
  const { query, pathname } = historySingleton.getCurrentLocation();
  const { expandAll: ignored, ...other } = query;
  replace({ pathname, query: { ...other }, state: { internal: true } });
}
/**
 * Resets the current deep link so that we can open the first invalid section after a server validate response
 */
export function toPrepareSubmit() {
  const { pathname, query: { section, ...other } = { section: undefined } } = historySingleton.getCurrentLocation();
  replace({ pathname, query: { ...other }, state: { internal: true } });
}

/**
 * Navigates to a new resource, creating a new entry in the browser history
 *
 * @param route route as string, or a <see ref="Location"/> object
 */
export function to(route: Partial<Location> | string) {
  let location = route;
  if (!isObject(route)) {
    location = { pathname: route as string, state: { internal: false } } as Location;
  }
  historySingleton.push(location);
}

/**
 * Updates the current browser URL, without adding a new entry in the browser history.
 * This prevents the user using
 * the back button to come back to the url that *was replaced*.
 * @param route
 */
export function replace(route: any) {
  let location: Location = route;
  if (!isObject(route)) {
    location = { pathname: route as string, state: { internal: false } } as Location;
  }
  historySingleton.replace(location);
}
/**
 * Used only when navigating within the case/draft entity hierarchy using deep linking to child entities
 */
export function toChildItem(route: any, sectionId: number, replaceCurrent: boolean = true) {
  const { query } = historySingleton.getCurrentLocation();
  const routeArg = { pathname: route, query: { ...query, section: sectionId || query.section }, state: { internal: true } } as Location;
  replaceCurrent ? replace(routeArg) : to(routeArg);
}

export function toMetroLineSection(route: any) {
  const { query } = historySingleton.getCurrentLocation();
  const routeArg = { pathname: route, query, state: { internal: true } } as Location;
  replace(routeArg);
}

export function isRouteInPath(path: string, route: string) {
  const reg = new RegExp(`^\/?${route}`);
  const search = path.search(reg);

  return search === 0;
}
